export type ProposalConflict={path:string;mine:unknown;latest:unknown};
const object=(v:unknown):v is Record<string,unknown>=>!!v&&typeof v==='object'&&!Array.isArray(v);
export const proposalFingerprint=(v:unknown):string=>JSON.stringify(v,(_k,value)=>object(value)?Object.fromEntries(Object.keys(value).sort().map(k=>[k,value[k]])):value);
const same=(a:unknown,b:unknown)=>proposalFingerprint(a)===proposalFingerprint(b);
const keyed=(v:unknown[]):v is Array<Record<string,unknown>&{id:string}>=>v.every(x=>object(x)&&typeof x.id==='string')&&new Set(v.map(x=>(x as {id:string}).id)).size===v.length;

/** Three-way merge against the actual loaded revision. Never turn a stale
 * full-document save into last-writer-wins. Conflicting values stay explicit. */
export function mergeProposal<T>(base:T,mine:T,latest:T):{value:T;conflicts:ProposalConflict[]}{
 const conflicts:ProposalConflict[]=[];
 function visit(b:unknown,m:unknown,l:unknown,path:string):unknown{
  if(same(m,b))return l;
  if(same(l,b)||same(m,l))return m;
  if(object(b)&&object(m)&&object(l)){
   return Object.fromEntries([...new Set([...Object.keys(b),...Object.keys(l),...Object.keys(m)])].flatMap(key=>{
    // Material snapshots are produced by the backbone from saved scope.
    const value=key==='material_quantities'?l[key]:visit(b[key],m[key],l[key],path?`${path}.${key}`:key);
    return value===undefined?[]:[[key,value]];
   }));
  }
  if(Array.isArray(b)&&Array.isArray(m)&&Array.isArray(l)&&keyed(b)&&keyed(m)&&keyed(l)){
   const ids=[...new Set([...l.map(x=>x.id),...m.map(x=>x.id)])];
   return ids.flatMap(id=>{
    const original=b.find(x=>x.id===id),local=m.find(x=>x.id===id),remote=l.find(x=>x.id===id);
    const label=String(local?.name??local?.label??remote?.name??remote?.label??id);
    const value=visit(original,local,remote,`${path} · ${label}`);
    return value===undefined?[]:[value];
   });
  }
  conflicts.push({path,mine:m,latest:l});return m;
 }
 return {value:visit(base,mine,latest,'') as T,conflicts};
}

export function proposalVersionConflict(error:unknown):boolean{
 const value=error as {message?:string;reason?:string;body?:{reason?:string}}|null;
 return /Proposal changed[.;]/i.test(value?.body?.reason??value?.reason??value?.message??'');
}
